import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import USStates from '../../../../i18n/us-states/us_states.json';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import JPStatesEn from '../../../../i18n/jp-states/states-en.json';
import JPStatesJp from '../../../../i18n/jp-states/states-jp.json';
import JPStatesZh from '../../../../i18n/jp-states/states-zh-cn.json';
import TWStates from '../../../../i18n/tw-states/tw-states.json';
import InputField from '../../components/InputField';
import SearchSelectImproved from '../../components/SearchSelectImproved';
import ValidationErrors from '../validationErrors';
import ValueFormatters from '../valueFormatters';
import { AddressFormatter } from '../../../../details/addresses/address/addressFormatter.service';
import { IState, IStateMap } from './state';

interface IStateFieldProps {
    autoFocus?: boolean;
    countryCode?: string;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    stateCodeFieldId?: string;
    stateCodeFieldValue?: string;
    stateNameFieldId?: string;
    stateNameFieldValue?: string;
    stateInputDisabled?: boolean;
    className?: string;
}

interface IStateFieldState {
    isDropdown: boolean;
    selectedState: IState | undefined;
    states: IState[];
    resetValue: boolean;
}

const JP_STATES_MAP: Record<string, IState[]> = {
    ja: JPStatesJp,
    en: JPStatesEn,
    'zh-CN': JPStatesZh,
};

const resolveJpStates = (locale: string, disabled?: boolean) => {
    const states = JP_STATES_MAP[locale] ?? JPStatesEn;
    if (disabled) {
        return states;
    } else {
        return states.filter((s) => {
            return s.code !== '国外';
        });
    }
};

const DEFAULT_SELECT_ITEM = { code: '', name: '' };

class StateField extends React.Component<IStateFieldProps, IStateFieldState> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly LABEL_US_ID: string;
    private readonly ERROR_REQUIRED_ID: string;
    private readonly ERROR_REQUIRED_US_ID: string;
    private readonly MAX_LENGTH_CODE: number;
    private readonly MAX_LENGTH_NAME: number;

    constructor(props: IStateFieldProps) {
        super(props);

        this.ID = 'state';
        this.LABEL_ID = 'label.state';
        this.LABEL_US_ID = 'label.state_US';
        this.ERROR_REQUIRED_ID = 'error.state.required';
        this.ERROR_REQUIRED_US_ID = 'error.state_US.required';
        this.MAX_LENGTH_CODE = 255;
        this.MAX_LENGTH_NAME = 255;

        const states = this.extractStates();
        this.state = {
            isDropdown: states && states.length > 0,
            resetValue: false,
            selectedState: this.resolveSelectedState(),
            states,
        };

        this.validate = this.validate.bind(this);
        this.renderField = this.renderField.bind(this);
        this.setFieldValues = this.setFieldValues.bind(this);
    }

    private resolveSelectedState() {
        const { countryCode, stateCodeFieldValue, stateNameFieldValue, intl } = this.props;
        const states = this.state ? this.state.states : this.extractStates();
        const stateCode = stateCodeFieldValue && stateCodeFieldValue.toUpperCase();
        const stateCodeKey = stateCode ? AddressFormatter.getStateKey(stateCode) : stateCode;

        if (countryCode === 'US' && stateCodeKey && (USStates as IStateMap)[stateCodeKey]) {
            return (USStates as IStateMap)[stateCodeKey];
        } else if (countryCode === 'JP') {
            return (
                // when we can not find the state, use stateNameFieldValue as fallback
                resolveJpStates(intl.locale, true).find((state) => {
                    return state.code === stateNameFieldValue && state.name;
                }) || {
                    code: '',
                    name: stateNameFieldValue ?? '',
                }
            );
        } else {
            return (
                states.find((state) => {
                    return (
                        state.name.toLowerCase() === stateNameFieldValue &&
                        stateNameFieldValue.toLowerCase()
                    );
                }) || {
                    code: '',
                    name: stateNameFieldValue ?? '',
                }
            );
        }
    }

    public componentDidUpdate(prevProps: IStateFieldProps) {
        const prevCountryCode = prevProps.countryCode;
        const prevStateNameFieldValue = prevProps.stateNameFieldValue;
        const { countryCode, stateNameFieldValue, stateCodeFieldValue, stateInputDisabled } =
            this.props;

        if (
            stateNameFieldValue !== prevStateNameFieldValue ||
            stateCodeFieldValue !== prevProps.stateCodeFieldValue
        ) {
            const newState = this.resolveSelectedState();
            this.onStateSet(newState);
        }
        if (
            (countryCode && prevCountryCode !== countryCode) ||
            (countryCode === 'JP' && prevProps.stateInputDisabled !== stateInputDisabled)
        ) {
            this.updateStates(prevCountryCode !== countryCode);
        }
    }

    public render() {
        const { isDropdown } = this.state;
        return (
            <Field
                name={isDropdown ? `${this.ID}-dropdown` : this.ID}
                validate={this.validate}
                className={this.props}
                render={this.renderField}
            />
        );
    }

    private validate(value: string): string {
        const { required } = this.props;
        const { isDropdown, selectedState } = this.state;
        let stateName = '';

        stateName = isDropdown && selectedState ? selectedState.name : value;

        const stateNameError = this.validateStateName(stateName, required || isDropdown);
        return stateNameError ? stateNameError : this.validateStateCode(required || isDropdown);
    }

    private validateStateCode(isRequired: boolean) {
        const { countryCode, intl, stateCodeFieldId } = this.props;
        const { selectedState } = this.state;

        if (!stateCodeFieldId) {
            return '';
        }

        if (isRequired && (!selectedState || !selectedState.code)) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(
                    countryCode && countryCode.toLowerCase() === 'us'
                        ? this.ERROR_REQUIRED_US_ID
                        : this.ERROR_REQUIRED_ID,
                ),
            );
        }

        if (selectedState && selectedState.code.length > this.MAX_LENGTH_CODE) {
            return ValidationErrors.fieldTooLongMessage(intl, this.MAX_LENGTH_CODE);
        }

        return '';
    }

    private validateStateName(stateName: string, isRequired: boolean) {
        const { countryCode, intl, stateNameFieldId } = this.props;

        if (!stateNameFieldId) {
            return '';
        }

        if (!stateName && isRequired) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(
                    countryCode && countryCode.toLowerCase() === 'us'
                        ? this.ERROR_REQUIRED_US_ID
                        : this.ERROR_REQUIRED_ID,
                ),
            );
        }

        if (stateName && stateName.length > this.MAX_LENGTH_NAME) {
            return ValidationErrors.fieldTooLongMessage(intl, this.MAX_LENGTH_NAME);
        }

        return '';
    }

    private updateStates(shouldResetValue: boolean) {
        const states = this.extractStates();
        this.setState({
            isDropdown: states.length > 0,
            resetValue: shouldResetValue,
            states,
        });
    }

    private extractStates() {
        const { countryCode, intl, stateInputDisabled } = this.props;
        if (countryCode === 'JP') {
            return resolveJpStates(intl.locale, stateInputDisabled);
        } else if (countryCode === 'US') {
            return Object.values(USStates);
        } else if (countryCode === 'TW') {
            return TWStates;
        } else {
            return [];
        }
    }

    private onStateSet(state?: IState) {
        this.setState({ selectedState: state });
    }

    private renderField(fieldProps: FieldProps): ReactNode {
        const { isDropdown, resetValue } = this.state;
        const { form } = fieldProps;
        if (resetValue) {
            // reset field value when country has been changed
            this.setFieldValues(form.setFieldValue, '', '');
            this.setState({ resetValue: false });
        }
        return isDropdown ? this.renderSelectField(fieldProps) : this.renderInputField(fieldProps);
    }

    private renderInputField({
        field,
        form: { touched, errors, setFieldValue, handleBlur },
    }: FieldProps): ReactNode {
        const { autoFocus, value, required, stateInputDisabled = false } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const onBlur = (event: React.FocusEvent<any>) => {
            setFieldValue(this.ID, ValueFormatters.trimExtraSpaces(event.target.value));
            handleBlur(event);
        };

        return (
            <InputField
                autoComplete="address-level1"
                labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                type="text"
                id={this.ID}
                maxLength={this.MAX_LENGTH_NAME}
                value={value}
                error={errorMessage}
                autoFocus={autoFocus}
                required={required}
                disabled={stateInputDisabled}
                onBlur={onBlur}
            />
        );
    }

    private renderSelectField({
        field,
        form: { touched, errors, setFieldValue, setFieldTouched },
    }: FieldProps): ReactNode {
        const { countryCode, stateInputDisabled } = this.props;
        const { states, selectedState } = this.state;
        const id = `${this.ID}-dropdown`;
        const isTouched = touched && (touched[this.ID] || touched[id]);
        const errorMessage = isTouched && errors && errors[id] ? errors[id] : '';
        const onStateSelect = ((state: IState) => {
            this.onStateSet(state);
            if (countryCode === 'JP') {
                this.setFieldValues(setFieldValue, '', state ? state.code : '');
            } else {
                this.setFieldValues(
                    setFieldValue,
                    state ? state.code : '',
                    state ? state.name : '',
                );
            }
        }).bind(this);

        return (
            <SearchSelectImproved
                autoComplete="address-level1"
                id={id}
                selectedItem={selectedState ?? DEFAULT_SELECT_ITEM}
                items={states}
                label={TranslationHelpers.getMessageDescriptor(
                    countryCode && countryCode.toLowerCase() === 'us'
                        ? this.LABEL_US_ID
                        : this.LABEL_ID,
                )}
                error={errorMessage}
                searchKey={'name'}
                onSelect={onStateSelect}
                setTouched={setFieldTouched}
                required={true}
                qaId={'state'}
                selectedItemKey={'code'}
                disabled={stateInputDisabled}
            />
        );
    }

    private setFieldValues(
        setFieldValue: (key: string, value: string) => void,
        stateCodeFieldValue: string,
        stateNameFieldValue: string,
    ) {
        const { stateCodeFieldId, stateNameFieldId, countryCode } = this.props;

        if (countryCode === 'TW') {
            if (stateCodeFieldId) {
                setFieldValue(stateCodeFieldId, stateNameFieldValue);
            }
        } else {
            if (stateCodeFieldId) {
                setFieldValue(stateCodeFieldId, stateCodeFieldValue);
            }
        }

        if (stateNameFieldId) {
            setFieldValue(stateNameFieldId, stateNameFieldValue);
        }
    }
}

export default injectIntl(StateField);
